/**
 * project210 — THE ONE REQUEST WRAPPER for the generated API client (T0405).
 *
 * Every generated call goes through here, so the base URL has one home and auth has one
 * attachment site. `src/services/api/generated/` is never hand-edited; this file is
 * hand-written and is the only place a request behaviour is decided.
 *
 * AUTH — the FE's existing token source, not a second one. `AuthUtils.getAuthHeaders()`
 * resolves the Firebase ID token (refreshing when needed, falling back to the stored
 * cookie) and builds the header. Public routes pass `{ anonymous: true }`: no header at
 * all, never an empty or malformed one. Before p210 the attachment was repeated in six
 * files (measured 2026-09-23); new calls attach it here and nowhere else.
 *
 * FAIL POSTURE — a non-2xx answer throws `ApiClientError` carrying the status and the
 * parsed body. Callers that must show a message read it from there. This is deliberate:
 * the hand-written modules parsed the body whatever the status, so a 500 read as data.
 */
import AuthUtils from "@/providers/firebase/authUtils";

const BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined)?.trim() ||
  "http://localhost:8000";

/** Matches the timeout the hand-written modules already applied (`DEFAULT_TIMEOUT_MS`). */
const DEFAULT_TIMEOUT_MS = 30_000;

/** Per-call options. orval passes this straight through from every generated call. */
export type ApiClientOptions = RequestInit & {
  /** Overrides the 30 s default. */
  timeoutMs?: number;
  /** Send no Authorization header (public routes). */
  anonymous?: boolean;
};

export class ApiClientError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.body = body;
  }
}

const apiClient = async <T>(
  url: string,
  options: ApiClientOptions = {},
): Promise<T> => {
  const {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    anonymous = false,
    headers,
    signal,
    ...init
  } = options;

  const authHeaders = anonymous ? {} : await AuthUtils.getAuthHeaders();

  // Abort on the caller's signal or the timeout, whichever comes first.
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  signal?.addEventListener("abort", () => controller.abort(), { once: true });

  let res: Response;
  try {
    res = await fetch(url.startsWith("http") ? url : `${BASE_URL}${url}`, {
      ...init,
      headers: { ...authHeaders, "Content-Type": "application/json", ...headers },
      signal: controller.signal,
    });
  } catch (err) {
    const aborted = (err as Error)?.name === "AbortError";
    throw new ApiClientError(
      aborted
        ? `Request timed out after ${timeoutMs} ms`
        : `Request failed: ${(err as Error)?.message ?? "network error"}`,
      0,
      undefined,
    );
  } finally {
    window.clearTimeout(timer);
  }

  const text = [204, 205, 304].includes(res.status) ? "" : await res.text();
  let body: unknown;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const stated = (body as { message?: unknown } | undefined)?.message;
    throw new ApiClientError(
      typeof stated === "string" && stated.trim()
        ? stated
        : `${res.status} ${res.statusText || "Request failed"}`,
      res.status,
      body,
    );
  }

  return body as T;
};

export { apiClient };
export default apiClient;
